<template>
  <section class="h-full mx-4" id="reportsAnswersManagement">
    <BaseTable
      v-if="visitLogins"
      :headers="tableHeaders"
      :list="visitLogins"
      :total-records="visitLoginsTotalCount"
      :items-per-page="itemsPerPage"
      :is-add-new-hidden="true"
      clickable
      table-id="reportsAnswersManagement"
      :hide-multiselect-delete="true"
      @refresh-data="onApplyFilters"
      @apply-filters="onApplyFilters"
      @reset-filters="onResetFilters"
      @table-item-clicked="onItemClick">
      <template #filters>
        <div class="grid grid-cols-4 gap-4 mx-10 my-5">
          <div class="space-y-4">
            <div class="">
              <label class="space-y-2 text-sm tracking-wide text-gray-400 uppercase">{{
                $t('qst-transaction-master.visitDateFrom')
              }}</label>
              <DateTimePicker
                ref="filterVisitDateFromPickerRef"
                v-model="filterVisitDateStringFrom"
                start-empty
                show-error-as-tooltip />
            </div>
            <div>
              <label class="space-y-2 text-sm tracking-wide text-gray-400 uppercase">{{
                $t('qst-transaction-master.visitDateTo')
              }}</label>
              <DateTimePicker
                ref="filterVisitDateToPickerRef"
                v-model="filterVisitDateStringTo"
                start-empty
                show-error-as-tooltip />
            </div>
          </div>
          <div class="space-y-4">
            <div>
              <label class="space-y-2 text-sm tracking-wide text-gray-400 uppercase">{{
                $t('baseObject.deleted')
              }}</label>
              <BaseToggle name="deleted" v-model="filterDeleted" />
            </div>
            <div>
              <label class="space-y-2 text-sm tracking-wide text-gray-400 uppercase">{{
                $t('baseObject.isActive')
              }}</label>
              <BaseTriStateToggle
                :label="$t('baseObject.isActive')"
                name="isActive"
                v-model="filterIsActive" />
            </div>
          </div>
        </div>
      </template>
    </BaseTable>
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useToastStore } from '@/stores/toast';
import BaseTable from '@/components/common/BaseTable.vue';
import BaseToggle from '@/components/common/BaseToggle.vue';
import BaseTriStateToggle from '@/components/common/BaseTriStateToggle.vue';
import { filterActive, filterData } from '@/utils/table';
import { isoDateToFullDate, isoDateToFullDateTime, parseDateManually } from '@/utils/date';
import { getVisitLoginsWithConditions } from '@/services/api/questionnaires';
import type { VisitLogin, VisitLoginTableFilterData } from '@/types/VisitLogin';
import DateTimePicker from '@/components/common/DateTimePicker.vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
const { t: $t } = useI18n({ useScope: 'global' });

const toastStore = useToastStore();
const router = useRouter();

const visitLogins = ref<VisitLogin[]>();
const visitLoginsTotalCount = ref<number>();
const itemsPerPage = ref<number>(25);

const filterDeleted = ref(false);
const filterVisitDateFromPickerRef = ref();
const filterVisitDateToPickerRef = ref();
const filterVisitDateStringFrom = ref<string>();
const filterVisitDateStringTo = ref<string>();
const filterIsActive = ref<boolean | undefined>();

const filterVisitDateFrom = computed(() => {
  return filterVisitDateStringFrom.value
    ? parseDateManually(filterVisitDateStringFrom.value)
    : null;
});
const filterVisitDateTo = computed(() => {
  return filterVisitDateStringTo.value ? parseDateManually(filterVisitDateStringTo.value) : null;
});

const tableHeaders = [
  { value: 'loginDateStamp', text: $t('visitPlans.visitDate'), type: 'text', active: true },
  {
    value: 'visit.salesPerson.name',
    text: $t('price-collection-document.salesPerson'),
    type: 'text',
    active: true,
  },
  {
    value: 'visit.salesPerson.code',
    text: $t('price-collection-document.salesPersonCode'),
    type: 'text',
    active: true,
  },
  {
    value: 'visit.traderSite.name',
    text: $t('price-collection-document.traderSite'),
    type: 'text',
    active: true,
  },
  { value: 'visit.visitRemarks', text: $t('material.remarks'), type: 'text', active: true },
  { value: 'visit.planned', text: $t('visitPlans.planned'), type: 'check', active: true },
];

onMounted(async () => {
  onResetFilters();
});

function onResetFilters() {
  filterVisitDateStringFrom.value = undefined;
  filterVisitDateStringTo.value = undefined;
  filterDeleted.value = false;
  filterIsActive.value = undefined;
  onApplyFilters(getDefaultParams());
}

function onApplyFilters(data: VisitLoginTableFilterData) {
  const fromDateValid = filterVisitDateFromPickerRef.value?.isValid ?? true;
  const toDateValid = filterVisitDateToPickerRef.value?.isValid ?? true;

  if (!fromDateValid || !toDateValid) {
    toastStore.error({ text: $t('messages.error.dateError') });
    return;
  }

  if (filterVisitDateFrom.value) {
    if (!isNaN(new Date(filterVisitDateFrom.value).getDate()))
      data.visitDateFrom = new Date(filterVisitDateFrom.value);
    else {
      toastStore.error({ text: $t('messages.error.dateError') });
      return;
    }
  } else data.visitDateFrom = new Date(0);

  if (filterVisitDateTo.value) {
    if (!isNaN(new Date(filterVisitDateTo.value).getDate()))
      data.visitDateTo = new Date(filterVisitDateTo.value);
    else {
      toastStore.error({ text: $t('messages.error.dateError') });
      return;
    }
  } else {
    // Set to end of current day (23:59:59.999) to include all records for today
    const endOfDay = new Date();
    endOfDay.setHours(23, 59, 59, 999);
    data.visitDateTo = endOfDay;
  }

  data.deleted = filterDeleted.value;
  data.isActive = filterIsActive.value;
  onRefreshData(data);
}

async function onRefreshData(data?: VisitLoginTableFilterData) {
  await loadData(data);
  if (!data) return;

  if (data.query && visitLogins.value) {
    visitLogins.value = filterData(visitLogins.value, data.query, [
      'visit.salesPerson.name',
      'visit.traderSite.name',
      'visit.salesPerson.code',
    ]);
  }

  if (data.isActive !== undefined && visitLogins.value)
    visitLogins.value = filterActive(visitLogins.value, data.isActive);
}

async function loadData(params?: VisitLoginTableFilterData) {
  if (!params) params = getDefaultParams();
  if (params.column) params.sortColumn = tableHeaders[params.column++].value;
  const res = await getVisitLoginsWithConditions(params);
  if (res) {
    visitLogins.value = res.data.map((item) => ({
      ...item,
      loginDateStamp: isoDateToFullDateTime(item.loginDateStamp) || item.loginDateStamp,
      visit: {
        ...item.visit,
        visitDate: isoDateToFullDate(item.visit?.visitDate) || item.visit?.visitDate,
      },
    })) as VisitLogin[];
    visitLoginsTotalCount.value = res.count;
  }
}

function getDefaultParams() {
  return {
    itemsPerPage: itemsPerPage.value,
    query: '',
    sortColumn: 'loginDateStamp',
    sortAscending: false,
  };
}

function onItemClick(item: VisitLogin) {
  router.push({
    name: 'questionnaires-report-details',
    params: { id: item.id },
  });
}
</script>
